import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiCheck, FiTarget, FiBookOpen } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import WhatsAppShare from '../components/WhatsAppShare';
import AuthorBio from '../components/AuthorBio';

const content = {
  en: {
    seoTitle: "Mercadona CrossFit Grocery List Barberà del Vallès",
    seoDesc: "Download the exact Mercadona grocery list in Barberà del Vallès to burn fat and fuel your CrossFit workouts. Free for local residents.",
    headline: "Barberà Residents: The Exact Mercadona Grocery List To Burn Fat & Fuel Your First Workout",
    subheadline: "Stop guessing in the aisles. Eat clean and loose weight without spending a single Euro more than you already do.",
    bulletsTitle: "Here is exactly what you get inside this free 1-page guide:",
    bullet1: "The Perimeter Hack: The exact aisles to hit (and the ones to avoid) at the Barberà Mercadona.",
    bullet2: "The Muscle Fuel: High-protein, cheap staples to survive intense workouts and build muscle.",
    bullet3: "The Tapas Loophole: What to order at local Barberà spots to stay lean.",
    proofQuote: "I lost 5 kg and added 12 kg to my deadlift. This works.",
    proofAuthor: "Notorious Member",
    scarcity: "Warning: Only 50 copies left for Barberà residents.",
    privacyLink: "Privacy Policy",
    imprintLink: "Imprint",
    blogLink: "Blog"
  },
  es: {
    seoTitle: "Guía de Compra Mercadona CrossFit Barberà del Vallès",
    seoDesc: "Descarga la lista exacta de la compra del Mercadona en Barberà del Vallès para quemar grasa y potenciar tu entrenamiento de CrossFit. Gratis para residentes locales.",
    headline: "Residentes de Barberà: La lista exacta de la compra de Mercadona para quemar grasa y potenciar tu primer entrenamiento",
    subheadline: "Deja de adivinar en los pasillos. Come limpio y pierde peso sin gastar un solo euro más de lo que ya gastas.",
    bulletsTitle: "Aquí tienes exactamente lo que obtienes en esta guía gratuita de 1 página:",
    bullet1: "El truco del perímetro: Los pasillos exactos que debes visitar en el Mercadona de Barberà.",
    bullet2: "Combustible para los músculos: Alimentos básicos, baratos y ricos en proteínas para entrenar duro.",
    bullet3: "El atajo de las tapas: Qué pedir en los locales de Barberà para mantenerte en forma.",
    proofQuote: "He perdido 5 kg y he sumado 12 kg a mi peso muerto. Esto funciona.",
    proofAuthor: "Notorious Member",
    scarcity: "Advertencia: Solo quedan 50 copias para residentes de Barberà.",
    privacyLink: "Política de Privacidad",
    imprintLink: "Aviso Legal",
    blogLink: "Blog"
  }
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "HealthAndBeautyBusiness",
  "name": "Barberà del Vallès Fit Guide",
  "description": "Local CrossFit and nutrition guides for Barberà del Vallès residents.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Nemesi Valls 42",
    "addressLocality": "Barberà del Vallès",
    "addressRegion": "Barcelona",
    "postalCode": "08210",
    "addressCountry": "ES"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "41.5167",
    "longitude": "2.1167"
  },
  "url": "https://barberadelvalles.fit"
};

export default function LandingPage({ lang }) {
  const t = content[lang];
  const formContainerRef = useRef(null);
  const currentUrl = "https://barberadelvalles.fit/";

  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://cdn.sendfox.com/js/embed.js";
    script.setAttribute('data-form', '1k9lrd');
    script.setAttribute('data-api', 'https://sendfox.com');
    script.async = true;
    const currentContainer = formContainerRef.current;
    if (currentContainer) {
      currentContainer.appendChild(script);
    }
    return () => {
      if (currentContainer && script.parentNode === currentContainer) {
        currentContainer.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="concrete-bg">
      <SEO 
        title={t.seoTitle} 
        description={t.seoDesc} 
        lang={lang} 
        schema={localBusinessSchema} 
      />
      <main className="max-w-5xl mx-auto px-6 py-12 lg:py-24 grid lg:grid-cols-12 gap-12 items-start">
        {/* Left Column: Copy */}
        <div className="lg:col-span-7 space-y-12">
          <div className="space-y-6">
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-[0.95] drop-shadow-2xl">
              {t.headline}
            </h1>
            <div className="h-1.5 w-24 bg-orange-600"></div>
            <p className="text-xl md:text-2xl text-slate-400 font-medium leading-relaxed max-w-xl">
              {t.subheadline}
            </p>
          </div>

          <div className="space-y-8 relative">
            <h2 className="text-2xl font-bold text-orange-500">{t.bulletsTitle}</h2>
            <ul className="space-y-6">
              {[t.bullet1, t.bullet2, t.bullet3].map((bullet, idx) => (
                <li key={idx} className="flex gap-4 group">
                  <div className="shrink-0 mt-1">
                    <div className="w-6 h-6 border-2 border-slate-700 flex items-center justify-center bg-slate-800 rotate-3 group-hover:rotate-0 transition-transform">
                      <SafeIcon icon={FiCheck} className="text-orange-500 font-bold" />
                    </div>
                  </div>
                  <p className="text-lg text-slate-300 leading-tight">{bullet}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right Column: Image, Bio & Form */}
        <div className="lg:col-span-5 space-y-12 lg:sticky lg:top-12">
          <div className="relative group">
            <div className="absolute -inset-4 bg-orange-600/5 -rotate-1 rounded-2xl"></div>
            <div className="relative flex flex-col md:flex-row lg:flex-col gap-6 items-center lg:items-start">
              <div className="relative shrink-0">
                <img 
                  src="https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&q=80&w=600" 
                  alt="Barberà Athlete" 
                  className="w-48 h-48 lg:w-full lg:h-64 object-cover grayscale hover:grayscale-0 transition-all duration-700 border-2 border-slate-800 shadow-2xl" 
                />
                <div className="absolute top-2 right-2 bg-orange-600 text-[10px] font-black px-2 py-1 uppercase tracking-widest">
                  Local Box
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-2xl font-display font-medium italic text-white leading-tight">
                  "{t.proofQuote}"
                </p>
                <p className="text-orange-500 font-bold uppercase text-sm tracking-widest">
                  — {t.proofAuthor}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Trust Signal: Author Bio */}
            <AuthorBio lang={lang} />
            
            {/* Conversion: Subscription Form */}
            <div id="sendfox-form-container" className="bg-slate-900 p-8 weathered-border shadow-[12px_12px_0px_0px_rgba(249,115,22,0.1)] min-h-[250px]" ref={formContainerRef} />
            
            <p className="text-center text-[10px] font-bold text-orange-500/60 italic uppercase tracking-widest px-4">
              {t.scarcity}
            </p>
            
            {/* Viral Loop Integration */}
            <WhatsAppShare url={currentUrl} lang={lang} />
          </div>
        </div>
      </main>

      <footer className="py-20 flex flex-col items-center gap-8">
        <div className="flex items-center gap-4 text-slate-500 text-xs font-bold uppercase tracking-widest">
          <Link to="/blog" className="hover:text-orange-500 transition-colors flex items-center gap-1">
            <SafeIcon icon={FiBookOpen} />
            {t.blogLink}
          </Link>
          <span className="text-slate-800">•</span>
          <Link to="/privacy" className="hover:text-orange-500 transition-colors">
            {t.privacyLink}
          </Link>
          <span className="text-slate-800">•</span>
          <Link to="/imprint" className="hover:text-orange-500 transition-colors">
            {t.imprintLink}
          </Link>
        </div>
        <div className="opacity-10 pointer-events-none flex justify-center overflow-hidden w-full">
          <div className="flex flex-col items-center">
            <SafeIcon icon={FiTarget} className="text-[20rem]" />
            <span className="font-display text-9xl -mt-20">BARBERÀ</span>
          </div>
        </div>
      </footer>
    </div>
  );
}